import { Component, type ErrorInfo, type ReactNode } from 'react';
import { de } from '../i18n/de';

/**
 * NFR8 / non-negotiable §1 — never a blank screen.
 *
 * This file is deliberately self-sufficient and slightly redundant with the rest
 * of the app. It imports nothing from `core/` or `data/`, hard-codes its own
 * keyboard rather than reading `layouts/de-CH.json`, and styles itself inline
 * rather than through custom properties. Every one of those is a dependency that
 * could be the thing that just crashed.
 *
 * What it guarantees: a keyboard, an output bar, and a Sprechen button. That is
 * the whole product reduced to its floor. It is not pretty and it is not meant to
 * be — it is what stands between a rendering bug and a person who cannot speak.
 *
 * Below this is `bootFloor.ts`, for when React itself does not start.
 */

// Swiss German QWERTZ, 11 × 3, no `ß` (Swiss Standard German writes `ss`), no
// shift states. Duplicated from layouts/de-CH.json on purpose — see above.
//
// It has **drifted from that layout deliberately** (ADR-027): the real keyboard traded its hyphen for a
// double-width space, and this one keeps eleven equal squares. Widening a key here would mean a span
// rule, and a span rule is code that has to be right on the one screen that exists for code being
// wrong. So the hyphen stays, which also makes safe mode the only place it can be typed at all.
const SAFE_ROWS: readonly string[][] = [
  ['Q', 'W', 'E', 'R', 'T', 'Z', 'U', 'I', 'O', 'P', 'Ü'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
  ['Y', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '-', ' '],
];

type Speaker = (text: string) => void;

let registeredSpeaker: Speaker | null = null;

/**
 * SpeechService registers itself here once it is alive (work item 1.6), so safe
 * mode speaks through the real provider chain when the chain is healthy. If it was
 * never registered — or if registering is what failed — we fall back to
 * `speechSynthesis` directly, which needs no unlock because this call originates
 * in a real tap.
 */
export function registerSafeSpeaker(speak: Speaker): void {
  registeredSpeaker = speak;
}

function safeSpeak(text: string): void {
  if (!text.trim()) return;
  try {
    if (registeredSpeaker) {
      registeredSpeaker(text);
      return;
    }
  } catch {
    // Fall through to the platform voice. Never let a broken provider chain mean
    // silence in the one mode that exists for broken things.
  }
  try {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'de-DE';
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  } catch {
    // Nothing left to try. The text is on screen at 48 px, which is the
    // VisualOnly outcome and still a way to be understood.
  }
}

const styles = {
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: 8,
    padding: '12px 8px',
    background: '#ffffff',
    color: '#000000',
    font: '400 20px -apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif',
  },
  notice: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    fontSize: 18,
  },
  output: {
    flex: '0 0 auto',
    minHeight: 96,
    maxHeight: 132,
    overflowY: 'auto',
    width: '60%',
    marginInline: 'auto',
    padding: '8px 12px',
    border: '3px solid #000000',
    borderRadius: 8,
    fontSize: 48,
    lineHeight: 1.2,
    textAlign: 'center',
    wordBreak: 'break-word',
  },
  keyboard: { display: 'flex', flexDirection: 'column', gap: 8, flex: '1 1 auto' },
  row: { display: 'flex', gap: 8, justifyContent: 'center' },
  key: {
    flex: '1 1 0',
    minWidth: 0,
    height: 99,
    fontSize: 34,
    background: '#ffffff',
    color: '#000000',
    border: '2px solid #000000',
    borderRadius: 8,
  },
  actions: {
    display: 'flex',
    gap: 8,
    width: '60%',
    marginInline: 'auto',
  },
  action: {
    flex: '1 1 0',
    height: 83,
    fontSize: 24,
    background: '#ffffff',
    color: '#000000',
    border: '3px solid #000000',
    borderRadius: 8,
  },
  speak: {
    flex: '2 1 0',
    height: 83,
    fontSize: 28,
    fontWeight: 700,
    background: '#000000',
    color: '#ffffff',
    border: '3px solid #000000',
    borderRadius: 8,
  },
} as const satisfies Record<string, React.CSSProperties>;

interface SafeModeState {
  failed: boolean;
  text: string;
}

interface SafeModeProps {
  children: ReactNode;
  /** Set by the dev-only throw route so tests can assert the boundary, not the app. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

export class SafeModeBoundary extends Component<SafeModeProps, SafeModeState> {
  override state: SafeModeState = { failed: false, text: '' };

  static getDerivedStateFromError(): Partial<SafeModeState> {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console only. Diagnostics persistence is a repository call, and a repository
    // is exactly the kind of thing that may have just thrown.
    console.error('[safe-mode] render failed', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  /**
   * Same casing rule as the real composer, spelled out again rather than imported.
   *
   * A keycap is a label, not the letter: `A` types `a`, and a sentence's first letter is capitalised.
   * The full rule lives in `core/composer/composer.ts`, but this file imports nothing from `core/` on
   * principle — and a crash screen that shouted while the app spoke German would be its own small
   * inconsistency, on the one screen the person least wants to puzzle over.
   */
  private readonly append = (char: string): void => {
    this.setState((s) => {
      if (char.length !== 1 || !/\p{L}/u.test(char)) return { text: s.text + char };
      const trimmed = s.text.replace(/\s+$/u, '');
      const starts = trimmed === '' || (trimmed !== s.text && /[.!?]$/u.test(trimmed));
      return { text: s.text + (starts ? char.toUpperCase() : char.toLowerCase()) };
    });
  };

  private readonly deleteChar = (): void => {
    this.setState((s) => ({ text: s.text.slice(0, -1) }));
  };

  private readonly deleteWord = (): void => {
    this.setState((s) => ({ text: s.text.replace(/\s*\S+\s*$/u, '') }));
  };

  private readonly speak = (): void => {
    safeSpeak(this.state.text);
  };

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <div style={styles.shell} data-testid="safe-mode">
        <div style={styles.notice}>
          <strong>{de.safeModeTitle}</strong>
          <span>{de.safeModeBody}</span>
        </div>

        <div style={styles.output} aria-live="polite" data-testid="safe-mode-output">
          {this.state.text}
        </div>

        <div style={styles.keyboard}>
          {SAFE_ROWS.map((row, rowIndex) => (
            <div key={rowIndex} style={styles.row}>
              {row.map((key) => (
                <button
                  key={key}
                  type="button"
                  style={styles.key}
                  onClick={() => this.append(key)}
                  aria-label={key === ' ' ? de.space : key}
                >
                  {key === ' ' ? '␣' : key}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div style={styles.actions}>
          <button type="button" style={styles.speak} onClick={this.speak}>
            {de.speak}
          </button>
          <button type="button" style={styles.action} onClick={this.deleteWord}>
            {de.deleteWord}
          </button>
          <button type="button" style={styles.action} onClick={this.deleteChar}>
            {de.deleteChar}
          </button>
          <button type="button" style={styles.action} onClick={() => window.location.reload()}>
            {de.safeModeReload}
          </button>
        </div>
      </div>
    );
  }
}
